<template>
    <CollapsibleWidget
        class="editable-array-widget editable-select-widget"
        :is-opened="isOpen"
        collapsed-height="47"
        @onChange="toggleOpen"
    >
        <template #name>{{ editable.label }}</template>

        <template #controls>
            <EditableRestrictionControl v-if="showRestrictionControl" :editable="editable" />
        </template>

        <div class="editable-widget__body">
            <div v-if="readOnly" class="editable-widget__body--readonly" title="Read Only" @click.stop.prevent></div>
            <div class="deliverable-value-item">
                <LabeledSelect
                    :label="EditableGroupScopeLabel"
                    :options="allOptions"
                    :placeholder="groupPlaceholder"
                    :model-value="getSelected(getEditableValue(editable, groupsAffectingEditable, null))"
                    @update="onAllChange($event, groupsAffectingEditable)"
                />
            </div>
            <div v-if="!editable.publisherPlatform">
                <LockableRow
                    v-for="(label, templateId) in templateLabels"
                    v-show="isOpen"
                    :key="getForKey(editable, templateId)"
                    :locked="isTemplateOverwrite(editable, groupsAffectingEditable, templateId)"
                    :tracked-clicks="['locked']"
                    @unlock="onUnlock(editable, groupsAffectingEditable, templateId)"
                >
                    <LabeledSelect
                        :label="label"
                        :label-color="getLabelColor(editable, groupsAffectingEditable, templateId)"
                        :options="getOptions(editable, groupsAffectingEditable, templateId)"
                        :model-value="getSelected(getEditableValue(editable, groupsAffectingEditable, templateId))"
                        @update="onUpdate($event, groupsAffectingEditable, templateId)"
                    />
                </LockableRow>
            </div>
        </div>
    </CollapsibleWidget>
</template>

<script lang="ts">
import { defineComponent, toRef, type PropType } from "vue";
import type { ArrayEditableValue, EditableValue, Editable } from "shared-types";

import CollapsibleWidget from "@/components/Campaign/widgets/CollapsibleWidget.vue";
import LabeledSelect from "@/components/Campaign/LabeledSelect.vue";
import LockableRow from "@/components/Campaign/LockableRow.vue";
import EditableRestrictionControl from "@/components/Campaign/Editables/EditableRestrictionControl.vue";

import { useToggleEditableItem } from "@/composables/useToggleEditableItem";
import { useOverwrite } from "@/composables/useOverwrite";
import { useEditableMethods } from "@/composables/useEditableMethods";
import { useEditable } from "@/composables/useEditable";

import { EditableGroupScopeLabel } from "@/enums/overwrites";
import * as editableUtils from "@/utils/editables";

export default defineComponent({
    name: "EditableArrayWidget",

    components: {
        EditableRestrictionControl,
        LockableRow,
        LabeledSelect,
        CollapsibleWidget
    },

    props: {
        editable: {
            type: Object as PropType<Editable>,
            required: true
        },

        groupsAffectingEditable: {
            type: Array as PropType<string[]>,
            required: true
        },

        allExpanded: {
            type: Boolean,
            default: true
        },

        readOnly: {
            type: Boolean,
            default: false
        },

        showRestrictionControl: {
            type: Boolean,
            default: true
        }
    },

    setup(props, { emit }) {
        const allExpanded = toRef(props, "allExpanded");
        const editable = toRef(props, "editable");

        const { isOpen, toggleOpen } = useToggleEditableItem({ allExpanded });
        const { getOverwrite, removeOverwrite, isTemplateOverwrite } = useOverwrite();
        const { getEditableValue, getLabelColor, update, getForKey } = useEditableMethods({ emit });
        const { templateLabels } = useEditable({ editable });

        return {
            isOpen,
            toggleOpen,
            getOverwrite,
            getEditableValue,
            getLabelColor,
            templateLabels,
            update,
            removeOverwrite,
            isTemplateOverwrite,
            getForKey
        };
    },

    data() {
        return {
            EditableGroupScopeLabel
        };
    },

    computed: {
        allOptions() {
            const allOptions: ArrayEditableValue[] = [];
            const allLabels: string[] = [];

            Object.keys(this.templateLabels).forEach(templateId => {
                const options = this.getEditableValue(this.editable, this.groupsAffectingEditable, templateId);

                if (Array.isArray(options)) {
                    options.forEach(option => {
                        if (!allLabels.includes(option.label)) {
                            allOptions.push(option);
                            allLabels.push(option.label);
                        }
                    });
                }
            });

            return allOptions;
        },

        groupPlaceholder() {
            const masterTemplateIds = Object.keys(this.templateLabels);
            const label = this.getSelectedLabel(editableUtils.getDefaultValue(this.editable, masterTemplateIds[0]));
            const sameDefaults = !masterTemplateIds.some(masterTemplateId => {
                const val = this.getSelectedLabel(editableUtils.getDefaultValue(this.editable, masterTemplateId));
                return val !== label;
            });

            return sameDefaults && label ? label : "Select";
        }
    },

    methods: {
        onAllChange(newValue: string, groupsAffectingEditable: string[]) {
            this.onUpdate(newValue, groupsAffectingEditable, null);
        },

        onUnlock(editable: Editable, groupsAffectingEditable: string[], templateId: string) {
            const overwrite = this.getOverwrite(editable, groupsAffectingEditable, templateId);
            this.removeOverwrite(overwrite._id);
        },

        onUpdate(newValue: string | undefined, groupsAffectingEditable: string[], templateId: string | null) {
            if (newValue === undefined) {
                return;
            }

            let editableValue = this.getEditableValue(
                this.editable,
                groupsAffectingEditable,
                templateId
            ) as ArrayEditableValue[];

            if (!editableValue) {
                editableValue = this.allOptions;
            }

            const options = editableValue.map(({ label, value }) => {
                const option: ArrayEditableValue = {
                    label,
                    value
                };

                if (option.value === newValue) {
                    option.selected = true;
                }

                return option;
            });

            this.update({
                editable: this.editable,
                updateData: { value: options },
                templateId,
                groupsAffectingEditable
            });
        },

        getSelected(options: EditableValue) {
            if (!Array.isArray(options)) {
                return null;
            }

            const selected = options.find(option => option.selected);
            return selected ? selected.value : null;
        },

        getSelectedLabel(options: ArrayEditableValue[]) {
            if (!Array.isArray(options)) {
                return null;
            }

            const selected = options.find(option => option.selected);
            return selected ? selected.label : null;
        },

        getOptions(editable: Editable, groupsAffectingEditable: string[], templateId: string | null) {
            return this.getEditableValue(editable, groupsAffectingEditable, templateId) as ArrayEditableValue[];
        }
    }
});
</script>
